// @flow
import DefaultMessagesManager from './DefaultMessagesManager'

// Types
import type { Message } from '../create'

// Constants
let DEFAULT_MANAGER = null

/**
 * Ger translationc manager from context or if context not
 * defined returns defaul messages manager which is created
 * on a first call of this function.
 * @param  {OBject} context
 * @return {MessagesManager}
 */
const getMessagesManager = (context?: any) => {
  if (context && context.mzIntl) {
    return context.mzIntl
  }
  if (!DEFAULT_MANAGER) {
    DEFAULT_MANAGER = new DefaultMessagesManager()
  }
  return DEFAULT_MANAGER
}

/**
 * One simple and independatnt function to format messages. It uses
 * message descriptor as requred argument and values with react context
 * object as options.
 *
 * When react context is not provided or there is no `mzIntl` field in
 * the context, then the message will be formatted with default formatter
 * without any formats defined.
 *
 * @param  {Message} descr
 * @param  {Object?} values
 * @param  {Object?} context
 * @return {string}
 */
export const formatMessage = (
  descr: string | Message,
  values: ?Object,
  context?: any,
  formats?: Object
) => {
  if (typeof descr === 'string') return descr
  const manager = getMessagesManager(context)
  const translatedMessage = manager.getTranslation(descr)

  // When no values provided we really do not need to worry
  // about formatting
  if (!values || !Object.keys(values).length) {
    return translatedMessage
  }

  try {
    // Get the formatter and format the translated message
    const formatter = manager.getFormatter(translatedMessage, formats)
    return formatter.format(values)
  } catch (e) {
    // Translated message could be broken by the translator. In this
    // case we should fallback to the original message and print the error
    manager.handleFormatError(e)
    const fallbackFormatter = manager.getFormatter(
      descr.defaultMessage,
      formats
    )
    return fallbackFormatter.format(values)
  }
}

/**
 * Wrapper around `isMessageReady` method from `MessagesManager` which
 * decide where to call the actual functoin - form manager which provided
 * in context or from default (module level) manager when IntlProvider
 * is not defined in the context.
 * @param  {Nessage} message
 * @param  {Object} context
 * @return {bool}
 */
export const isMessageReady = (message: string | Message, context?: any) => {
  if (typeof message === 'string') return true
  const manager = getMessagesManager(context)
  return manager.isMessageReady(message)
}

/**
 * Wrapper around `loadMessage` method from `MessagesManager` which
 * decide where to call the actual functoin - form manager which provided
 * in context or from default (module level) manager when IntlProvider
 * is not defined in the context.
 * @param  {Nessage} message
 * @param  {Object} context
 * @return {bool}
 */
export const loadMessage = (
  message: string | Message,
  handler: Function,
  context?: any
) => {
  if (typeof message === 'string') return
  const manager = getMessagesManager(context)
  manager.loadMessage(message, handler)
}

/**
 * Returns true if all locale necessary stuff (moment locale, react-intl locale)
 * are loaded and ready to be used
 * @param  {Object}  context
 * @return {Boolean}
 */
export const isLocaleReady = (context?: any) => {
  const manager = getMessagesManager(context)
  return manager.isLocaleReady()
}

/**
 * Calls given callback when all locale necessary stuff (moment locale, react-intl
 * locale) loaded and ready to be used
 * @param  {Function} callback
 */
export const whenLocaleReady = (callback: Function, context?: any) => {
  const manager = getMessagesManager(context)
  return manager.whenLocaleReady(callback)
}

export const getActiveLocale = (context?: any) => {
  const manager = getMessagesManager(context)
  return manager.getActiveLocale()
}

export const getActiveLocaleDescriptor = (context?: any) => {
  const manager = getMessagesManager(context)
  return manager.getActiveLocaleDescriptor()
}
